import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, FileText } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { CTABanner } from '../components/CTABanner';
export function InsightsPage() {
  const posts = [{
    id: 1,
    title: 'The Future of Cloud Computing in African Enterprises',
    date: 'October 15, 2023',
    category: 'Cloud Infrastructure',
    excerpt: 'As digital transformation accelerates across the continent, we explore how hybrid cloud architectures are providing the perfect balance of security and scalability for African businesses.'
  }, {
    id: 2,
    title: 'Securing National Data: Best Practices for Government Agencies',
    date: 'September 28, 2023',
    category: 'Cybersecurity',
    excerpt: 'With cyber threats on the rise, government bodies must adopt zero-trust architectures. Learn the foundational steps to securing sensitive citizen data.'
  }, {
    id: 3,
    title: 'Bridging the Digital Divide: The Role of Private-Public Partnerships',
    date: 'September 10, 2023',
    category: 'Digital Inclusion',
    excerpt: 'How collaboration between tech companies like Invigo and government agencies is accelerating internet penetration in rural communities.'
  }, {
    id: 4,
    title: 'Optimizing Business Processes with AI and Automation',
    date: 'August 22, 2023',
    category: 'Business Process',
    excerpt: 'Discover how implementing intelligent automation can reduce operational costs by up to 40% while significantly improving service delivery times.'
  }];
  return <main className="w-full pt-20" data-id="element-407">
      {/* Page Header */}
      <section className="bg-navy-900 py-20 relative overflow-hidden" data-id="element-408">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-409" />
        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-410">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-411">
            Insights & <span className="text-tech-green" data-id="element-412">News</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-slate-300 max-w-2xl" data-id="element-413">
            Thought leadership, industry trends, and updates from the forefront
            of Africa's digital transformation.
          </motion.p>
        </div>
      </section>

      <section className="py-24 bg-slate-50" data-id="element-414">
        <div className="max-w-7xl mx-auto px-4" data-id="element-415">
          <div className="grid lg:grid-cols-3 gap-8" data-id="element-416">
            {/* Blog Posts Grid */}
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-8" data-id="element-417">
              {posts.map((post, idx) => <motion.article key={post.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 group flex flex-col" data-id="element-418">
                  <div className="p-6 flex flex-col h-full" data-id="element-419">
                    <div className="flex items-center justify-between mb-4" data-id="element-420">
                      <span className="text-xs font-semibold text-tech-blue bg-blue-50 px-3 py-1 rounded-full" data-id="element-421">
                        {post.category}
                      </span>
                      <span className="text-xs text-slate-400" data-id="element-422">
                        {post.date}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-3 group-hover:text-tech-blue transition-colors" data-id="element-423">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-424">
                      {post.excerpt}
                    </p>
                    <a href="#" className="inline-flex items-center text-sm font-semibold text-tech-green group-hover:text-emerald-500 transition-colors mt-auto" data-id="element-425">
                      Read Article{' '}
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-426" />
                    </a>
                  </div>
                </motion.article>)}
            </div>

            {/* Sidebar */}
            <div className="space-y-8" data-id="element-427">
              {/* Download Profile Card */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="bg-gradient-to-br from-navy-800 to-navy-900 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden" data-id="element-428">
                <div className="absolute -right-10 -top-10 text-white/5" data-id="element-429">
                  <FileText className="w-48 h-48" data-id="element-430" />
                </div>
                <div className="relative z-10" data-id="element-431">
                  <h3 className="text-2xl font-bold mb-4" data-id="element-432">Company Profile</h3>
                  <p className="text-slate-300 mb-8 text-sm leading-relaxed" data-id="element-433">
                    Download our comprehensive corporate profile to learn more
                    about our services, methodology, and extensive track record
                    across Africa.
                  </p>
                  <button className="w-full py-3 bg-tech-green text-white rounded-xl font-semibold hover:bg-emerald-400 transition-colors flex items-center justify-center gap-2 shadow-lg" data-id="element-434">
                    <Download className="w-5 h-5" data-id="element-435" />
                    Download PDF
                  </button>
                </div>
              </motion.div>

              {/* Newsletter Signup */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm" data-id="element-436">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-437">
                  Stay Updated
                </h3>
                <p className="text-slate-600 text-sm mb-6" data-id="element-438">
                  Subscribe to our newsletter for the latest insights on
                  enterprise technology.
                </p>
                <form className="space-y-4" data-id="element-439">
                  <input type="email" placeholder="Your email address" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-tech-blue outline-none bg-slate-50" data-id="element-440" />
                  <button className="w-full py-3 bg-navy-900 text-white rounded-xl font-semibold hover:bg-navy-800 transition-colors" data-id="element-441">
                    Subscribe
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner data-id="element-442" />
    </main>;
}